/** ModernFusion — Hero. Minimalist with glassmorphism effects. */

import React from 'react';
import { motion } from 'framer-motion';
import { CalendarDays, MapPin, ChevronDown } from 'lucide-react';
import { MODERN_EASE } from '../theme';
import type { ModernContent } from '../content';

export function HeroSection({
  content,
  onRsvp,
}: {
  content: ModernContent['hero'];
  onRsvp: () => void;
}) {
  return (
    <header
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 py-20 text-center"
      style={{
        background: 'linear-gradient(135deg, #87a878 0%, #c9a9a6 100%)',
      }}
    >
      {/* Glassmorphism card */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: MODERN_EASE }}
        className="relative z-10 max-w-2xl rounded-3xl border border-white/30 bg-white/20 p-10 backdrop-blur-md"
      >
        <p className="mb-3 text-sm uppercase tracking-[0.3em] text-white/80">
          {content.kicker}
        </p>
        <h1 className="font-serif text-5xl font-semibold text-white sm:text-6xl">
          {content.title}
        </h1>
        <p className="mt-4 text-lg italic text-white/90">{content.subtitle}</p>

        <div className="mt-8 flex flex-col items-center gap-3 text-sm text-white/80 sm:flex-row sm:justify-center sm:gap-8">
          <span className="inline-flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            {content.dateLabel}
          </span>
          <span className="inline-flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            {content.venueShort}
          </span>
        </div>

        <p className="mt-6 text-sm text-white/70">Hosted by {content.host}</p>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
          onClick={onRsvp}
          className="mt-8 rounded-full bg-white/90 px-8 py-3 text-sm font-semibold uppercase tracking-wider text-[#5a7a52] shadow-lg backdrop-blur-sm transition-colors hover:bg-white"
        >
          RSVP
        </motion.button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
        className="absolute bottom-6"
      >
        <ChevronDown className="h-6 w-6 animate-bounce text-white/80" />
      </motion.div>
    </header>
  );
}
